import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import { useAuth } from "./AuthContext";

/* ====================================================== */
/* Context                                                */
/* ====================================================== */

const CompanyContext = createContext(null);

/* ====================================================== */
/* Provider                                               */
/* ====================================================== */

export const CompanyProvider = ({ children }) => {
  const {
    companies = [],
    companyId,
    selectCompany,
    isLoggedIn,
    loading: authLoading,
    activeCompany,
  } = useAuth();

  const [initializing, setInitializing] = useState(true);

  /* ====================================================== */
  /* 🔧 Ensure LocalStorage Sync (CRITICAL FIX)             */
  /* ====================================================== */

  const persistCompanyId = (id) => {
    if (!id) return;
    localStorage.setItem("companyId", id); // 🔥 required for apiClient
  };

  /* ====================================================== */
  /* 🚀 Workspace Initialization                           */
  /* ====================================================== */

  useEffect(() => {
    if (authLoading) return;

    if (!isLoggedIn) {
      localStorage.removeItem("companyId"); // cleanup
      setInitializing(false);
      return;
    }

    if (!companies.length) {
      setInitializing(false);
      return;
    }

    const stored = localStorage.getItem("companyId");

    /* -------------------------------------------------- */
    /* Restore stored workspace                           */
    /* -------------------------------------------------- */

    if (stored && companies.some((c) => c._id === stored)) {
      if (stored !== companyId) {
        selectCompany(stored);
      }

      persistCompanyId(stored); // 🔥 ensure sync
      setInitializing(false);
      return;
    }

    /* -------------------------------------------------- */
    /* Default fallback                                   */
    /* -------------------------------------------------- */

    const first = companies[0]?._id;

    if (first) {
      selectCompany(first);
      persistCompanyId(first); // 🔥 CRITICAL
    }

    setInitializing(false);
  }, [
    companies,
    isLoggedIn,
    companyId,
    selectCompany,
    authLoading,
  ]);

  /* ====================================================== */
  /* 🔄 Switch Company                                     */
  /* ====================================================== */

  const switchCompany = useCallback(
    (id) => {
      if (!id) return;

      const exists = companies.some((c) => c._id === id);
      if (!exists) return;

      selectCompany(id);
      persistCompanyId(id); // 🔥 CRITICAL FIX
    },
    [companies, selectCompany]
  );

  /* ====================================================== */
  /* 📦 Context Value                                      */
  /* ====================================================== */

  const value = useMemo(
    () => ({
      companies,
      companyId,
      activeCompany,
      switchCompany,
      initializing,

        membership: activeCompany?.membership || null, // ✅ ADD THIS
    }),
    [companies, companyId, activeCompany, switchCompany, initializing]
  );

  /* ====================================================== */
  /* ⏳ Loading Guard                                      */
  /* ====================================================== */

  if (authLoading || initializing) {
    return (
      <div className="h-screen flex items-center justify-center text-gray-500">
        Loading workspace...
      </div>
    );
  }

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

/* ====================================================== */
/* Hook                                                   */
/* ====================================================== */

export const useCompany = () => {
  const ctx = useContext(CompanyContext);

  if (!ctx) {
    throw new Error(
      "useCompany must be used inside CompanyProvider"
    );
  }

  return ctx;
};